import React from "react";
import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  Link,
} from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import {
  AlertCircle,
  Info,
  Calendar,
  MapPin,
  Check,
  Music,
  Users,
  CreditCard,
  CheckCircle,
  Eye,
  GraduationCap,
} from "lucide-react";
import "./RegistrationFlow.css";
import ModernMaterialSteps from "../../../components/ui/ModernMaterialSteps";
import { ALL_STEPS } from "./constants/registrationSteps";
import useRegistrationState from "../../../hooks/useRegistrationState";
import useEventImages from "../../../hooks/useEventImages";
import OptimizedImage from "../../../components/ui/OptimizedImage";
import {
  Event,
  RegistrationData,
  PricingSettings,
  CriteriaSettings,
  Step,
  OptimizedImages,
  Participant,
  Routine,
  StepComponentProps
} from "../../../types/registration";
import RegistrationDataMonitor from "../../../components/debug/RegistrationDataMonitor";
import RegistrationDataTester from "../../../components/debug/RegistrationDataTester";
import GlowingCard from "../../../components/ui/GlowingCard";
import StepNavigationFooter from "../../../components/registration/StepNavigationFooter";

// Get icon component based on step ID
const getStepIcon = (stepId: string) => {
  switch (stepId) {
    case 'overview':
      return <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />;
    case 'competition':
      return <Music className="w-4 h-4 sm:w-5 sm:h-5" />;
    case 'dancers':
      return <Users className="w-4 h-4 sm:w-5 sm:h-5" />;
    case 'teachers':
      return <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5" />;
    case 'observers':
      return <Eye className="w-4 h-4 sm:w-5 sm:h-5" />;
    case 'review':
      return <Check className="w-4 h-4 sm:w-5 sm:h-5" />;
    case 'payment':
      return <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />;
    case 'success':
      return <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />;
    default:
      return null;
  }
};

// Combined header content component with event info and steps
const CombinedHeaderContent: React.FC<{
  isDarkMode: boolean;
  event: Event | null;
  optimizedImages: OptimizedImages;
  placeholders: OptimizedImages;
  steps: Step[];
  currentStepIndex: number;
  onStepClick: (index: number) => void;
}> = ({ isDarkMode, event, optimizedImages, placeholders, steps, currentStepIndex, onStepClick }) => (
  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-6">
    {/* Left: Event title and logo */}
    <div className="flex items-center flex-shrink-0">
      <div
        className={`mr-3 p-2 rounded-full ${
          isDarkMode ? "bg-blue-600/20" : "bg-primary/10"
        }`}
      >
        {optimizedImages.logoImage ? (
          <div className="h-5 w-5 sm:h-6 sm:w-6 overflow-hidden rounded-full">
            <OptimizedImage
              src={optimizedImages.logoImage}
              placeholderSrc={placeholders.logoImage}
              alt={event?.name || "Event logo"}
              objectFit="contain"
              className=""
            />
          </div>
        ) : (
          <Calendar className={`h-5 w-5 sm:h-6 sm:w-6 text-primary`} />
        )}
      </div>
      <div>
        <h1
          className={`font-bold text-lg sm:text-xl ${
            isDarkMode ? "text-[var(--text-primary)]" : "text-gray-900"
          }`}
        >
          {event?.name}
        </h1>
        <div className="flex items-center">
          <span className={`text-xs uppercase tracking-wider mr-2 ${
            isDarkMode ? "text-[var(--text-secondary)]" : "text-gray-500"
          }`}>
            REGISTRATION
          </span>
          <div
            className={`w-2 h-2 rounded-full ${
              isDarkMode ? "bg-[var(--color-success)]" : "bg-success"
            }`}
          ></div>
          <span
            className={`text-xs ml-1 ${
              isDarkMode ? "text-[var(--color-success)]" : "text-success"
            }`}
          >
            OPEN
          </span>
        </div>
      </div>
    </div>

    {/* Center: Registration steps */}
    <div className="flex-1 lg:flex lg:justify-center">
      <div className="overflow-x-auto hide-scrollbar">
        <div className="steps-wrapper flex justify-center gap-2 sm:gap-3 py-1">
          {steps.map((step, index) => {
            // Determine step status
            const isCompleted = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;
            const isClickable = index <= currentStepIndex + 1;
            
            return (
              <div key={step.id} className="flex flex-col items-center">
                {/* Step circle */}
                <button
                  onClick={() => isClickable && onStepClick(index)}
                  disabled={!isClickable}
                  className={`
                    w-7 h-7 sm:w-8 sm:h-8 lg:w-9 lg:h-9 flex items-center justify-center rounded-full
                    transition-all duration-300 ease-in-out 
                    ${isClickable ? 'cursor-pointer hover:brightness-105' : 'cursor-not-allowed opacity-70'}
                    ${isCompleted 
                      ? isDarkMode 
                        ? 'bg-[var(--color-success)] text-[var(--text-primary)]' 
                        : 'bg-green-600 text-white' 
                      : isCurrent
                        ? isDarkMode
                          ? 'bg-[var(--color-blue-primary)] text-[var(--text-primary)] border-2 border-[var(--border-color-focus)] shadow-[var(--shadow-md)]' 
                          : 'bg-primary-600 border-2 border-primary-400 shadow-md text-white'
                        : isDarkMode
                          ? 'bg-[var(--background-elevated)] text-[var(--text-secondary)]' 
                          : 'bg-gray-100 text-gray-700'
                    }
                    ${!isCompleted && !isCurrent && !isDarkMode ? 'border border-gray-300' : ''}
                    ${!isCompleted && !isCurrent && isDarkMode ? 'border border-[var(--border-color)]' : ''}
                  `}
                >
                  {isCompleted ? (
                    <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                  ) : (
                    getStepIcon(step.id) || <span className={`
                      text-xs font-bold
                      ${isDarkMode 
                        ? isCurrent ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
                        : isCurrent ? 'text-white' : 'text-gray-700'
                      }
                    `}>
                      {index + 1}
                    </span>
                  )}
                </button>
                
                {/* Step label - hidden on mobile, visible on larger screens */}
                <div className="mt-1 text-center hidden sm:block">
                  <span className={`
                    text-xs font-medium leading-tight inline-block
                    transition-colors duration-300
                    ${isCurrent
                      ? isDarkMode ? 'text-[var(--color-blue-primary)] font-bold' : 'text-primary-600 font-bold'
                      : isCompleted
                        ? isDarkMode ? 'text-[var(--text-secondary)]' : 'text-gray-800'
                        : isDarkMode ? 'text-[var(--text-tertiary)]' : 'text-gray-500'
                    }
                  `}>
                    {step.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>

    {/* Right: Date and location */}
    <div className="flex items-center gap-3 flex-shrink-0">
      <div className="flex flex-col items-end">
        <div className={`text-sm font-medium ${
          isDarkMode ? "text-[var(--text-primary)]" : "text-gray-900"
        }`}>{event?.date}</div>
        <div
          className={`text-xs ${
            isDarkMode ? "text-[var(--text-tertiary)]" : "text-gray-500"
          }`}
        >
          {event?.location}
        </div>
      </div>
      <div
        className={`p-2 rounded-lg bg-primary/10 text-primary border border-primary/20`}
      >
        {optimizedImages.venueImage ? (
          <div className="h-4 w-4 sm:h-5 sm:w-5 overflow-hidden rounded">
            <OptimizedImage
              src={optimizedImages.venueImage}
              placeholderSrc={placeholders.venueImage}
              alt={event?.venue?.name || "Venue"}
              objectFit="cover"
              className=""
            />
          </div>
        ) : (
          <MapPin className="h-4 w-4 sm:h-5 sm:w-5" />
        )}
      </div>
    </div>
  </div>
);

/**
 * Main Registration Flow Component
 * Orchestrates the multi-step registration process
 */
const RegistrationFlow: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  
  // Use our custom hook to manage all registration state
  const { state, handlers } = useRegistrationState(ALL_STEPS);

  // Destructure state for easier access
  const {
    event,
    loading,
    error,
    steps,
    currentStepIndex,
    registrationId,
    pricingSettings,
    criteriaSettings,
    registrationData,
    regParticipants
  } = state;
  
  // Use our custom hook for image optimization with type assertion
  // Using any as a temporary workaround for the TypeScript compiler error
  const imageData = useEventImages(event) as any;
  const optimizedImages = imageData.optimizedImages;
  const placeholders = imageData.placeholders;

  // Destructure handlers for easier access
  const {
    handleStepClick,
    handleNext,
    handleBack,
    updateRegistrationData,
    addParticipant,
    updateParticipant,
    removeParticipant,
    addRoutine,
    updateRoutine,
    removeRoutine
  } = handlers;

  // --- RENDER ---

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-500">Loading event data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div
          className={`p-4 rounded-lg max-w-lg w-full ${isDarkMode ? "bg-red-500/10 border border-red-500/20 text-red-400" : "alert alert-error shadow-lg"}`}
        >
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold mb-2">Registration Error</h3>
              <p className="text-sm">{error}</p>
              <div className="mt-4 flex justify-end">
                <button 
                  className={`px-4 py-2 rounded ${isDarkMode ? "bg-red-500/20 hover:bg-red-500/30 text-red-300" : "bg-red-100 hover:bg-red-200 text-red-700"}`}
                  onClick={() => window.location.href = `/user/events/${event?.id}`}
                >
                  Return to Event
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handler functions for OptimizedImage
  const handleImageLoad = () => {
    // No-op function to satisfy TypeScript
  };
  
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // No-op function to satisfy TypeScript
    console.error("Image failed to load", e);
  };

  // Main registration flow
  return (
    <div className="registration-flow h-screen flex flex-col">
      {/* Combined Header with Event Info, Steps, and Alert */}
      <header className="relative z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          {isDarkMode ? (
            <GlowingCard
              glowColor="from-blue-600/10 via-purple-600/10 to-violet-600/10"
              glowIntensity="blur-xl"
              cardOpacity="bg-[#1a1f2d]/90"
              className="rounded-lg shadow-lg overflow-hidden"
            >
              {/* Main header content */}
              <div className="p-2 pb-1">
                <CombinedHeaderContent 
                  isDarkMode={isDarkMode} 
                  event={event} 
                  optimizedImages={optimizedImages} 
                  placeholders={placeholders}
                  steps={steps}
                  currentStepIndex={currentStepIndex}
                  onStepClick={handleStepClick}
                />
              </div>
              
              {/* Alert banner - flush with header */}
              <div className="bg-gradient-to-r from-amber-600/10 to-orange-600/10 border-t border-amber-600/20 p-1">
                <div className="flex items-center justify-center">
                  <div className="p-1.5 rounded-full mr-3 bg-[var(--color-warning)]/20">
                    <Info className="h-4 w-4 text-[var(--color-warning)]" />
                  </div>
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    Your spot is not reserved until you complete the registration process.
                  </p>
                </div>
              </div>
            </GlowingCard>
          ) : (
            <div className="bg-gradient-to-r from-white to-base-100 border-gray-200 shadow-sm rounded-lg overflow-hidden">
              {/* Main header content */}
              <div className="p-2 pb-1">
                <CombinedHeaderContent 
                  isDarkMode={isDarkMode} 
                  event={event} 
                  optimizedImages={optimizedImages} 
                  placeholders={placeholders}
                  steps={steps}
                  currentStepIndex={currentStepIndex}
                  onStepClick={handleStepClick}
                />
              </div>
              
              {/* Alert banner - flush with header */}
              <div className="bg-amber-50 border-t border-amber-100 p-1">
                <div className="flex items-center justify-center">
                  <div className="p-1.5 rounded-full mr-3 bg-amber-100">
                    <Info className="h-4 w-4 text-amber-600" />
                  </div>
                  <p className="text-sm font-medium text-amber-800">
                    Your spot is not reserved until you complete the registration process.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main content with step components */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <Routes>
          {steps.map((step: Step) => (
            <Route
              key={step.id}
              path={`/${step.path}`}
              element={
                <step.component
                  event={event}
                  pricingSettings={pricingSettings}
                  criteriaSettings={criteriaSettings}
                  registrationData={{
                    ...registrationData,
                    registrationId: registrationId,
                    
                    // Pass step-specific data and functions
                    ...(step.id === 'dancers' && {
                      dancers: regParticipants.dancers || registrationData.registrationDancers || [],
                      dancersFromRoutines: registrationData.dancersFromRoutines || [],
                      addParticipant: (participant: Participant) => addParticipant(participant, 'dancer'),
                      updateParticipant,
                      removeParticipant
                    }),
                    
                    ...(step.id === 'teachers' && {
                      teachers: regParticipants.teachers || registrationData.registrationTeachers || [],
                      addParticipant: (participant: Participant) => addParticipant(participant, 'teacher'),
                      updateParticipant,
                      removeParticipant
                    }),
                    
                    ...(step.id === 'observers' && {
                      observers: regParticipants.observers || registrationData.registrationObservers || [],
                      addParticipant: (participant: Participant) => addParticipant(participant, 'observer'),
                      updateParticipant,
                      removeParticipant
                    }),
                    
                    ...(step.id === 'competition' && {
                      routines: registrationData.routines || [],
                      selectedRoutineIds: registrationData.competition?.selectedRoutineIds || [],
                      mappings: registrationData.competition?.mappings || {},
                      routineOptions: registrationData.competition?.routineOptions || {},
                      addRoutine,
                      updateRoutine,
                      removeRoutine
                    }),
                    
                    ...(step.id === 'review' && {
                      routines: registrationData.routines || [],
                      regParticipants: {
                        dancers: regParticipants.dancers || registrationData.registrationDancers || [],
                        teachers: regParticipants.teachers || registrationData.registrationTeachers || [],
                        observers: regParticipants.observers || registrationData.registrationObservers || []
                      }
                    })
                  }}
                  optimizedImages={optimizedImages}
                  placeholderImages={placeholders}
                  updateRegistrationData={(data: any, isComplete?: boolean) => {
                    return updateRegistrationData(step.id, data, isComplete);
                  }}
                  onNext={handleNext}
                  onBack={handleBack}
                  isDarkMode={isDarkMode}
                  currentStep={currentStepIndex}
                  totalSteps={steps.length}
                  // Pass navigation component as prop so each step can control it
                  renderNavigation={(stepOnNext?: () => void, stepOnBack?: () => void, canGoNext: boolean = true, nextLabel?: string) => (
                    <StepNavigationFooter
                      currentStep={currentStepIndex}
                      totalSteps={steps.length}
                      onBack={stepOnBack || handleBack}
                      onNext={stepOnNext || handleNext}
                      canGoBack={currentStepIndex > 0}
                      canGoNext={canGoNext}
                      nextLabel={nextLabel || (currentStepIndex === steps.length - 1 ? "Complete" : "Continue")}
                    />
                  )}
                />
              }
            />
          ))}

          {/* Default redirect to first step */}
          <Route
            path="*"
            element={
              <div className="flex justify-center items-center h-full">
                {steps.length > 0 ? (
                  <Link
                    to={`/user/events/${event?.id}/register/${
                      steps[0]?.path || "overview"
                    }`}
                    className="btn btn-primary"
                  >
                    Start Registration
                  </Link>
                ) : (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                )}
              </div>
            }
          />
          </Routes>
        </div>
      </div>
      
      {/* Debug Components - Only show if we have a registration ID */}
      {registrationId && (
        <>
          <RegistrationDataMonitor registrationId={registrationId} />
          <RegistrationDataTester registrationId={registrationId} />
        </>
      )}
    </div>
  );
};

export default RegistrationFlow; 